import type { ComponentProps } from "react";
import { tv } from "tailwind-variants";
import { t } from "ttag";

import type { Category } from "@/shared/domains";
import { CATEGORY_SLUG } from "@/shared/domains";
import { Badge, Checkbox, ShowcaseCard } from "@/shared/ui";

const styles = tv({
  base: [
    "flex-row border-secondary-100 bg-gradient-to-r from-secondary-50 to-secondary-50 p-0 transition-colors duration-300 hover:border-secondary-100 sm:min-h-30",
    "has-data-[state=checked]:from-primary-400 has-data-[state=checked]:to-primary-700 has-data-[state=checked]:[&_[data-slot=showcase-card-description]]:text-white has-data-[state=checked]:[&_[data-slot=showcase-card-title]]:text-white has-data-[state=checked]:[&_[data-slot=showcase-card-title]]:duration-300 has-data-[state=checked]:[&>*]:text-white",
  ],
  variants: {
    category: {
      [CATEGORY_SLUG.MICRODOSING]:
        "has-data-[state=checked]:from-tasman-400 has-data-[state=checked]:to-tasman-700",
      [CATEGORY_SLUG.LONGEVITY]:
        "has-data-[state=checked]:from-bismark-400 has-data-[state=checked]:to-bismark-800",
      [CATEGORY_SLUG.WEIGHT_LOSS]:
        "has-data-[state=checked]:from-secondary-500 has-data-[state=checked]:to-secondary-800",
      [CATEGORY_SLUG.TESTOSTERONE]:
        "has-data-[state=checked]:from-merlot-800 has-data-[state=checked]:to-merlot-950",
      [CATEGORY_SLUG.HAIR_GROWTH]: "",
    },
  },
});

type CategoryCardProps = ComponentProps<typeof Checkbox.Input> & {
  category: Category;
};

export const CategoryCard = ({
  category,
  disabled,
  ...props
}: CategoryCardProps) => {
  return (
    <ShowcaseCard.Root
      className={styles({ category: category.slug })}
      disabled={disabled}
    >
      <div className="flex w-full flex-col gap-3 overflow-hidden rounded-xl p-4">
        <ShowcaseCard.Header className="flex-row gap-2">
          <ShowcaseCard.Title>{category.name}</ShowcaseCard.Title>

          {category.isNew && (
            <Badge
              color="green"
              size="x-small"
              className="translate-y-0.5 sm:translate-y-1"
            >
              {t`New!`}
            </Badge>
          )}

          <Checkbox.Input hidden size="lg" disabled={disabled} {...props} />
        </ShowcaseCard.Header>

        <ShowcaseCard.Description
          title={category.description}
          className="text-base font-medium duration-300 sm:line-clamp-2"
        >
          {category.description}
        </ShowcaseCard.Description>
      </div>

      {category.thumbnail && (
        <ShowcaseCard.Thumbnail
          src={category.thumbnail}
          className="aspect-square h-auto w-24 rounded-tl-4xl object-right sm:h-full sm:w-31"
        />
      )}
    </ShowcaseCard.Root>
  );
};
